import * as Yup from "yup";

import CreateTargetForm, {
  CreateTargetFormValues,
} from "components/forms/add-target";
import { Modal, OfficeButton } from "components";

import { createTarget } from "../../_graphql/visal/mutations/target";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { wrapClick } from "utils";

export interface CreateTarget_createTarget {
  __typename: "Target";
  id: string;
  broker_id: string;
  target_name: string;
  start_date: string;
  end_date: string;
  base_currency: string;
}

export interface CreateTarget {
  createTarget: CreateTarget_createTarget;
}

export interface CreateTargetVariables {
  broker_id: string;
  target_name: string;
  start_date: string;
  end_date: string;
  base_currency: string;
}

const validationSchema = Yup.object().shape({
  broker_id: Yup.string().required("Broker ID is required"),
  target_name: Yup.string().required("Target name is required"),
  start_date: Yup.string().required("Start date is required"),
  end_date: Yup.string()
    .required("End date is required")
    .test(
      "end-date-after-start-date",
      "End date must be after start date",
      function (value) {
        const { start_date } = this.parent;
        return !start_date || !value || new Date(value) >= new Date(start_date);
      }
    ),
  base_currency: Yup.string().required("Base currency is required"),
});

export default function CreateTargetContainer({
  open,
  setOpen,
  refetch,
  brokerId = "1",
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
  brokerId?: string;
}) {
  const [execute, { loading }] = useMutation<
    CreateTarget,
    CreateTargetVariables
  >(createTarget, {
    refetchQueries: ["fetchTargets"],
  });

  const form = useFormik<CreateTargetFormValues>({
    initialValues: {
      broker_id: brokerId,
      target_name: "",
      start_date: "",
      end_date: "",
      base_currency: "GHS",
    },
    validationSchema,
    onSubmit: async (variables, helpers) => {
      execute({ variables })
        .then(() => {
          toast.success("Target created successfully");
          setOpen(false);
          helpers.resetForm();
          refetch && refetch();
        })
        .catch((err) => {
          toast.error(err.message);
        });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Create Target"
      description="Provide target details below"
      renderActions={() => (
        <OfficeButton
          type="button"
          size="sm"
          disabled={loading}
          onClick={wrapClick(form.handleSubmit)}
        >
          {loading ? "Please wait..." : "Create Target"}
        </OfficeButton>
      )}
    >
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative"
        role="alert"
      >
        <div className="font-bold">Target Information</div>
        <div>
          This feature lets you set different goals in any way you want. Think
          of these goals as a way to track how much brokerage (commission) is
          earned from a specific reinsured company. You can set goals for the
          year, for each quarter, or for any other time period you choose.
        </div>
        <div className="mt-2">
          The system also allows you to configure badges linked to these goals.
          These badges help reinsurance underwriters and managers see the
          rewards or prizes available when the goal is met.
        </div>
      </div>
      <CreateTargetForm form={form} />
    </Modal>
  );
}
